// <s-bg-video> — decorative background video playlist with a pause/play toggle.
// data-sources: comma-separated video URLs. One URL loops forever; several play one after
// another and wrap back to the first. The video is muted, inline and aria-hidden; the
// [data-bg-video-toggle] button lets visitors stop the motion (WCAG 2.2.2). Reduced-motion
// visitors start paused. One component per file.
class SBgVideo extends HTMLElement {
  #ac = null;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const video = this.querySelector("video");
    const toggle = this.querySelector("[data-bg-video-toggle]");
    if (!video) return;

    const sources = (this.dataset.sources || "").split(",").map((s) => s.trim()).filter(Boolean);
    video.loop = sources.length <= 1;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduce.matches) {
      video.removeAttribute("autoplay");
      video.pause();
    }

    // Playlist: on end, move to the next clip and keep playing (unless the visitor paused).
    // A clip that fails to load is skipped so the playlist never stalls.
    video.addEventListener("error", () => {
      if (sources.length <= 1) return;
      this.#index = (this.#index + 1) % sources.length;
      video.src = sources[this.#index];
      video.play().catch(() => {});
    }, { signal });

    video.addEventListener("ended", () => {
      if (sources.length <= 1) return;
      this.#index = (this.#index + 1) % sources.length;
      video.src = sources[this.#index];
      video.play().catch(() => {});
    }, { signal });

    const sync = () => {
      const paused = video.paused && !video.ended;
      toggle?.setAttribute("aria-pressed", String(paused));
      toggle?.querySelector("[data-pause-icon]")?.toggleAttribute("hidden", paused);
      toggle?.querySelector("[data-play-icon]")?.toggleAttribute("hidden", !paused);
    };

    toggle?.addEventListener("click", () => {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    }, { signal });
    video.addEventListener("play", sync, { signal });
    video.addEventListener("pause", sync, { signal });
    sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get("s-bg-video")) {
  customElements.define("s-bg-video", SBgVideo);
}
